import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
	AlertCircle,
	Archive,
	Check,
	ChevronLeft,
	ChevronRight,
	Download,
	Info,
	Send,
	Trash2,
	X,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { Resolver } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ApproveApplicationModal from "../components/ApproveApplicationModal";
import KYCReview from "../components/kyc/KYCReview";
// Import step components (will create these next)
import Step1CompanyBasics from "../components/kyc/Step1CompanyBasics";
import Step2WalletTechnical from "../components/kyc/Step2WalletTechnical";
import Step3Ownership from "../components/kyc/Step3Ownership";
import Step4Compliance from "../components/kyc/Step4Compliance";
import Step5Limits from "../components/kyc/Step5Limits";
import Step6Contacts from "../components/kyc/Step6Contacts";
import LoadingSpinner from "../components/LoadingSpinner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { resolveFieldConfig } from "../config/fieldConfig";
import { evaluateCondition } from "../config/fieldConfig/conditionUtils";
import { validateFormForSubmit } from "../config/fieldConfig/formValidation";
import { generateZodSchemaFromFieldConfig } from "../config/fieldConfig/generateZodSchema";
import { useAuth } from "../context/AuthContext";
import { useWallets } from "../context/WalletContext";
import {
	useKYBAddComment,
	useKYBCreate,
	useKYBDelete,
	useKYBForm,
	useKYBSave,
	useKYBSubmit,
} from "../hooks";
import { useAsyncJob } from "../hooks/useAsyncJob";
import useConfirmDialog from "../hooks/useConfirmDialog";
import { cn } from "../lib/utils";
import { ApiError } from "../services/api/axiosClient";
import { transformDBKYBToKYCFormData } from "../services/data/api/kycProvider";
import { trpc } from "../trpc";
import type { KYCFormData } from "../types";
import {
	dbKeyToUiKey,
	type FlattenedError,
	flattenFormErrors,
	getFieldToStepMap,
} from "../utils/fieldErrorUtils";
import {
	isAlreadySubmittedError,
	KYB_STAGES,
	type KybStagedError,
	toUserMessage,
} from "../utils/kybSubmitErrors";
import { notifications } from "../utils/notifications";

/** Config step (from fieldConfig) to display step (UI order) */
const CONFIG_TO_DISPLAY_STEP: Record<number, number> = {
	1: 1,
	2: 5,
	3: 2,
	4: 3,
	5: 4,
	6: 6,
};

const KYCForm: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const { isAdmin, hasAdminAccess, user } = useAuth();
	const { confirm } = useConfirmDialog();
	const { wallets, getWallet, updateWallet } = useWallets();
	const queryClient = useQueryClient();
	const trpcUtils = trpc.useUtils();

	const { data: queryFormData, isLoading: formLoading } = useKYBForm(
		id,
		!!id && id !== "new",
	);
	const saveMutation = useKYBSave();
	const createMutation = useKYBCreate();
	const deleteMutation = useKYBDelete();
	const submitMutation = useKYBSubmit();
	const addCommentMutation = useKYBAddComment();

	const pdfJob = useAsyncJob({
		onComplete: (j) => {
			if (j.downloadUrl)
				window.open(j.downloadUrl, "_blank", "noopener,noreferrer");
			notifications.success("PDF ready");
		},
		onError: () => notifications.error("PDF generation failed"),
	});
	const bundleJob = useAsyncJob({
		onComplete: (j) => {
			if (j.downloadUrl)
				window.open(j.downloadUrl, "_blank", "noopener,noreferrer");
			notifications.success("Attachments ready");
		},
		onError: () => notifications.error("Bundling failed"),
	});

	const [formData, setFormData] = useState<KYCFormData | null>(null);
	const [currentStep, setCurrentStep] = useState(1);
	const [showReview, setShowReview] = useState(false);
	const [showContextChangeBanner, setShowContextChangeBanner] = useState(false);
	// Track previous country/entity to detect changes for soft-delete
	const prevFieldContextRef = useRef<{
		country: string;
		entity: string;
	} | null>(null);
	const [showComments, setShowComments] = useState(false);
	const [newComment, setNewComment] = useState("");
	const [commentAuthor, setCommentAuthor] = useState("");
	const [isInternalComment, setIsInternalComment] = useState(false);
	const [showApprovalModal, setShowApprovalModal] = useState(false);
	const [validationErrors, setValidationErrors] = useState<
		FlattenedError[] | null
	>(null);
	const formInitializedFromQueryRef = useRef<string | null>(null);
	/** Guard: run "form not found" logic only once per id to avoid updateWallet→invalidate→refetch loop */
	const formNotFoundHandledRef = useRef<string | null>(null);
	/** Guard: run "new KYB init" logic once per route key to avoid repeated wallet.update calls. */
	const newFormInitHandledRef = useRef<string | null>(null);
	const newFormInitInFlightRef = useRef(false);
	/** Auto-open comments once per submitted/approved form for admin reviewers. */
	const commentsAutoOpenedForFormRef = useRef<string | null>(null);

	// Custom resolver: build Zod schema from current form values (for dependsOn/appliesWhen)
	const rhfResolver = useCallback(
		async (
			values: KYCFormData,
			context: unknown,
			options: Parameters<Resolver<KYCFormData>>[2],
		) => {
			const vals = values as unknown as Record<string, unknown>;
			const ctx = {
				incorporationCountry: String(values.countryOfIncorporation ?? ""),
				entityType: String(values.entityType ?? ""),
				flowType: "KYB" as const,
			};
			const resolved = resolveFieldConfig(ctx);
			const schema = generateZodSchemaFromFieldConfig(resolved.fields, vals);
			const result = await zodResolver(schema)(vals, context, options as never);
			return result as ReturnType<Resolver<KYCFormData>>;
		},
		[],
	) as Resolver<KYCFormData>;

	const form = useForm<KYCFormData>({
		values: formData ?? undefined,
		defaultValues: {} as KYCFormData,
		resolver: rhfResolver,
		mode: "onSubmit",
	});

	useEffect(() => {
		if (
			!hasAdminAccess ||
			!formData?.id ||
			(formData.status !== "submitted" && formData.status !== "approved")
		)
			return;
		if (commentsAutoOpenedForFormRef.current === formData.id) return;
		commentsAutoOpenedForFormRef.current = formData.id;
		setShowComments(true);
	}, [hasAdminAccess, formData?.id, formData?.status]);

	// Calculate progress percentage based on config-driven required fields per step
	const calculateProgress = useCallback((formData: KYCFormData): number => {
		const totalSteps = 6;

		const isFilled = (value: unknown): boolean => {
			if (value === undefined || value === null) return false;
			if (typeof value === "string") return value.trim() !== "";
			if (typeof value === "boolean") return true;
			if (typeof value === "number") return true;
			if (Array.isArray(value)) return value.length > 0;
			if (typeof value === "object") {
				const obj = value as Record<string, unknown>;
				if (obj.name !== undefined) {
					return Boolean(obj.name && String(obj.name).trim() !== "");
				}
				return Object.values(obj).some((v) =>
					Boolean(v && String(v).trim() !== ""),
				);
			}
			return false;
		};

		const { fields } = resolveFieldConfig({
			incorporationCountry: formData.countryOfIncorporation,
			entityType: formData.entityType,
			flowType: "KYB",
		});

		const formValues = formData as unknown as Record<string, unknown>;
		let completedSteps = 0;

		for (let step = 1; step <= totalSteps; step++) {
			const stepFields = fields.filter(
				(f) =>
					f.step === step &&
					f.required &&
					(!f.dependsOn || evaluateCondition(f.dependsOn, formValues)),
			);
			const stepComplete =
				stepFields.length > 0 &&
				stepFields.every((f) => isFilled(formValues[f.fieldId]));

			if (stepComplete) completedSteps++;
		}

		return Math.round((completedSteps / totalSteps) * 100);
	}, []);

	// Update wallet KYB progress
	const updateWalletKYBProgress = useCallback(
		async (walletId: string, formData: KYCFormData) => {
			const wallet = getWallet(walletId);
			if (!wallet) return;

			// Pointer-safety guard: never silently switch the wallet's primary
			// KYB form via a progress update. The previous version of this
			// function wrote `kyb_form_id: formData.id` unconditionally — that
			// meant opening any orphan or sibling draft and clicking Next
			// rebound the wallet to it, orphaning the real submission. This
			// is the bug shape that produced Humanity Link / Tender Trade
			// multi-form drift.
			//
			// Rule: progress + completion fields update freely. The primary-
			// form pointer (kyb_form_id) is written ONLY when:
			//   1. The wallet has no pointer yet (first-bind), OR
			//   2. The pointer already references this form (no change in
			//      meaning — just a no-op refresh).
			// Any other case is refused with a warning so we can spot
			// regressions in logs.
			const existing = wallet.kyc_form_completed ?? {};
			const currentPointer = existing.kyb_form_id;
			const progress = calculateProgress(formData);
			const completedAt =
				progress === 100 ? new Date().toISOString() : existing.completed_at;
			const completedBy =
				progress === 100 ? formData.submittedBy : existing.completed_by;

			const canWritePointer = !currentPointer || currentPointer === formData.id;

			if (!canWritePointer) {
				// Silent corruption guard: don't touch the pointer at all,
				// but still surface progress/completion if the doc is the
				// wallet's currently-bound form. If it isn't, skip entirely
				// to avoid attaching progress numbers to the wrong record.
				console.warn(
					"[kyb] updateWalletKYBProgress: refusing to repoint wallet",
					{
						walletId,
						currentPointer,
						attemptedPointer: formData.id,
					},
				);
				return;
			}

			await updateWallet(walletId, {
				kyc_form_completed: {
					...existing,
					kyb_form_id: currentPointer ?? formData.id,
					kyb_progress: progress,
					completed: progress === 100,
					completed_at: completedAt,
					completed_by: completedBy,
				},
			});
		},
		[getWallet, updateWallet, calculateProgress],
	);

	// Sync form state from TanStack Query once per form id (avoids overwriting user edits)
	useEffect(() => {
		if (id && id !== "new") formInitializedFromQueryRef.current = null;
	}, [id]);

	useEffect(() => {
		if (
			!queryFormData ||
			formInitializedFromQueryRef.current === queryFormData.id
		)
			return;
		formInitializedFromQueryRef.current = queryFormData.id;
		setFormData(queryFormData);
		setCurrentStep(queryFormData.currentStep);
		const searchParams = new URLSearchParams(location.search);
		const isReadonly = searchParams.get("readonly") === "true";
		if (
			isReadonly ||
			queryFormData.status === "submitted" ||
			queryFormData.status === "approved" ||
			queryFormData.percentComplete === 100
		) {
			setShowReview(true);
		}
	}, [queryFormData, location.search]);

	// Scroll to first error section when validation errors are set (review screen)
	useEffect(() => {
		if (!validationErrors || validationErrors.length === 0 || !showReview)
			return;
		const stepsWithErrors = [...new Set(validationErrors.map((e) => e.step))];
		const firstStep = Math.min(...stepsWithErrors);
		const timer = requestAnimationFrame(() => {
			const el = document.querySelector(
				`[data-kyc-section-step="${firstStep}"]`,
			);
			el?.scrollIntoView({ behavior: "smooth", block: "start" });
		});
		return () => cancelAnimationFrame(timer);
	}, [validationErrors, showReview]);

	useEffect(() => {
		if (id && id !== "new" && !formLoading && !queryFormData) {
			// Run only once per form id — updateWallet invalidates wallets → refetch → re-render → loop
			if (formNotFoundHandledRef.current === id) return;
			formNotFoundHandledRef.current = id;

			const walletId = new URLSearchParams(location.search).get("walletId");
			if (walletId) {
				const wallet = getWallet(walletId);
				if (wallet?.kyc_form_completed?.kyb_form_id === id) {
					updateWallet(walletId, {
						kyc_form_completed: {
							...wallet.kyc_form_completed,
							kyb_form_id: undefined,
							kyb_progress: undefined,
						},
					}).finally(() => navigate(isAdmin ? "/kyc" : "/"));
					return;
				}
			}
			navigate(hasAdminAccess ? "/kyc" : "/");
		}
	}, [
		id,
		formLoading,
		queryFormData,
		navigate,
		isAdmin,
		hasAdminAccess,
		location.search,
		getWallet,
		updateWallet,
	]);

	// Handle "new" form creation — decision tree to avoid duplicate forms
	// See KYC_FORM_ID_DECISION_TREE.md for full diagram
	// biome-ignore lint/correctness/useExhaustiveDependencies: this init intentionally runs once per route key; broader deps can retrigger mid-flight and strand /kyc/new
	useEffect(() => {
		if (id !== "new") return;
		const routeKey = `${id}:${location.search}`;
		if (
			newFormInitHandledRef.current === routeKey ||
			newFormInitInFlightRef.current
		)
			return;
		newFormInitInFlightRef.current = true;
		const initNew = async () => {
			const searchParams = new URLSearchParams(location.search);
			const walletId = searchParams.get("walletId");

			// Decision tree: when walletId present, try to resume existing draft before creating
			if (walletId) {
				const wallet = getWallet(walletId);

				// 1. Wallet has kyb_form_id? → status-aware routing
				//
				// Previous version: on non-draft, this block CLEARED the wallet
				// pointer ("kyb_form_id: undefined") then fell through to a
				// create call. That actively destroyed valid bindings for
				// submitted/approved forms — root cause of multi-form drift
				// on every /kyc/new visit after submission. Status table now:
				//   draft                       → navigate (resume)
				//   submitted/under_review      → navigate read-only (view)
				//   approved                    → navigate read-only (view)
				//   rejected                    → navigate (partner can hit
				//                                  "Start new application" CTA
				//                                  which sends force_repoint)
				//   not found / stale           → safe to clear + fall through
				const existingFormId = wallet?.kyc_form_completed?.kyb_form_id;
				if (existingFormId) {
					let existingForm: KYCFormData | null = null;
					let formExists = false;
					try {
						const raw = await trpcUtils.kyb.getById.fetch({
							id: existingFormId,
						});
						existingForm = transformDBKYBToKYCFormData(raw);
						formExists = !!existingForm;
					} catch {
						// Form not found → leave formExists = false
					}

					if (formExists && existingForm) {
						switch (existingForm.status) {
							case "draft":
								navigate(`/kyc/${existingFormId}`, { replace: true });
								return;
							case "submitted":
							case "under_review":
							case "approved":
								// Real submission on file. Do NOT clear pointer,
								// do NOT create a new form. Take the operator/
								// partner to read-only view.
								navigate(`/kyc/${existingFormId}?readonly=true`, {
									replace: true,
								});
								return;
							case "rejected":
								// Show the rejected form. The form's own UI
								// renders a "Start new application" CTA that
								// explicitly passes force_repoint when needed.
								navigate(`/kyc/${existingFormId}`, { replace: true });
								return;
						}
					} else {
						// Pointer references a form that doesn't exist (stale or
						// deleted). Safe to clear before falling through to
						// getDraftByWalletId / create.
						if (
							wallet &&
							wallet.kyc_form_completed?.kyb_form_id === existingFormId
						) {
							await updateWallet(walletId, {
								kyc_form_completed: {
									...wallet.kyc_form_completed,
									kyb_form_id: undefined,
									kyb_progress: undefined,
								},
							});
						}
					}
				}

				// 2. No valid kyb_form_id → Check API for draft by wallet (e.g. wallet link was lost)
				const draftRaw = await trpcUtils.kyb.getDraftByWalletId.fetch({
					wallet_id: walletId,
				});
				if (draftRaw) {
					const draftByWallet = transformDBKYBToKYCFormData(draftRaw);
					if (wallet) {
						const nextProgress = draftByWallet.percentComplete ?? 0;
						const currentLink = wallet.kyc_form_completed?.kyb_form_id;
						const currentProgress = wallet.kyc_form_completed?.kyb_progress;
						if (
							currentLink !== draftByWallet.id ||
							currentProgress !== nextProgress
						) {
							await updateWallet(walletId, {
								kyc_form_completed: {
									...wallet.kyc_form_completed,
									kyb_form_id: draftByWallet.id,
									kyb_progress: nextProgress,
								},
							});
						}
					}
					navigate(`/kyc/${draftByWallet.id}`, { replace: true });
					return;
				}
			}

			// 3. No existing draft → Create new form (BFF stores wallet_id and updates wallet)
			const newForm = await createMutation.mutateAsync(walletId ?? undefined);
			// BFF updates wallet on create when walletId provided — skip extra PATCH to reduce 429
			if (walletId) {
				queryClient.invalidateQueries({ queryKey: ["wallets"] });
			}
			setFormData(newForm);
			navigate(`/kyc/${newForm.id}`, { replace: true });
		};
		initNew()
			.then(() => {
				newFormInitHandledRef.current = routeKey;
			})
			.catch((err) => {
				notifications.error(
					err instanceof Error
						? err.message
						: "Failed to initialize KYB application.",
				);
			})
			.finally(() => {
				newFormInitInFlightRef.current = false;
			});
	}, [id, location.search]);

	// Auto-save (debounced) via TanStack Query mutation — no wallet update to avoid rate limits
	// Track last-saved snapshot to skip saves when nothing changed
	const lastSavedSnapshotRef = useRef<string>("");
	// Set while a submit is in flight so a debounced autosave can't fire mid-submit
	// and clobber the just-submitted status back to "in-progress".
	const submittingRef = useRef(false);
	const saveMutationMutate = saveMutation.mutate;
	useEffect(() => {
		if (
			!formData?.id ||
			submittingRef.current ||
			formData.status === "submitted" ||
			formData.status === "under_review" ||
			formData.status === "approved" ||
			formData.status === "rejected"
		)
			return;

		// Snapshot user-editable fields only (exclude auto-save metadata)
		const { lastSavedAt: _ls, percentComplete: _pc, ...userFields } = formData;
		const snapshot = JSON.stringify(userFields);
		if (snapshot === lastSavedSnapshotRef.current) return;

		const timeoutId = setTimeout(() => {
			// A submit may have started during the debounce window — never let a
			// trailing autosave overwrite the submitted status.
			if (submittingRef.current) return;
			const progress = calculateProgress(formData);
			const updatedForm: KYCFormData = {
				...formData,
				percentComplete: progress,
				lastSavedAt: new Date().toISOString(),
				status:
					progress === 100
						? ("in-progress" as const)
						: formData.status === "draft"
							? ("draft" as const)
							: formData.status,
			};
			lastSavedSnapshotRef.current = snapshot;
			saveMutationMutate(updatedForm);
			// Skip updateWallet on auto-save — only update on explicit Save & Close, goToStep, submit, approve
			notifications.success("Saved");
		}, 4000);
		return () => clearTimeout(timeoutId);
	}, [formData, calculateProgress, saveMutationMutate]);

	const updateFormData = (updates: Partial<KYCFormData>) => {
		if (!formData) return;

		const countryChanging =
			updates.countryOfIncorporation !== undefined &&
			updates.countryOfIncorporation !== formData.countryOfIncorporation;
		const entityChanging =
			updates.entityType !== undefined &&
			updates.entityType !== formData.entityType;

		if (countryChanging || entityChanging) {
			const newCountry =
				updates.countryOfIncorporation ?? formData.countryOfIncorporation;
			const newEntity = updates.entityType ?? formData.entityType;

			const prevConfig = resolveFieldConfig({
				incorporationCountry: formData.countryOfIncorporation,
				entityType: formData.entityType,
				flowType: "KYB",
			});
			const nextConfig = resolveFieldConfig({
				incorporationCountry: newCountry,
				entityType: newEntity,
				flowType: "KYB",
			});

			const nextIds = new Set(nextConfig.fields.map((f) => f.fieldId));
			const prevIds = new Set(prevConfig.fields.map((f) => f.fieldId));

			const nowHidden = prevConfig.fields
				.filter((f) => !nextIds.has(f.fieldId))
				.map((f) => f.fieldId);
			const nowRestored = nextConfig.fields
				.filter((f) => !prevIds.has(f.fieldId))
				.map((f) => f.fieldId);

			if (nowHidden.length > 0 || nowRestored.length > 0) {
				const hiddenValues: Record<string, unknown> = {
					...(formData.hiddenFieldValues ?? {}),
				};
				const restoredUpdates: Record<string, unknown> = {};

				for (const fieldId of nowHidden) {
					const val = (formData as unknown as Record<string, unknown>)[fieldId];
					if (val !== undefined) hiddenValues[fieldId] = val;
				}
				for (const fieldId of nowRestored) {
					if (hiddenValues[fieldId] !== undefined) {
						restoredUpdates[fieldId] = hiddenValues[fieldId];
						delete hiddenValues[fieldId];
					}
				}

				setFormData({
					...formData,
					...updates,
					...(restoredUpdates as Partial<KYCFormData>),
					hiddenFieldValues: hiddenValues,
				});
				setShowContextChangeBanner(true);
				prevFieldContextRef.current = {
					country: newCountry,
					entity: newEntity,
				};
				return;
			}
		}

		setFormData({ ...formData, ...updates });
	};

	const goToStep = useCallback(
		(step: number) => {
			// Don't let navigation-triggered saves fire mid-submit and revert status.
			if (submittingRef.current) return;
			if (formData) {
				const updatedData = {
					...formData,
					currentStep: step,
					percentComplete: calculateProgress({
						...formData,
						currentStep: step,
					}),
				};
				setFormData(updatedData);
				saveMutation.mutate(updatedData);
				if (updatedData.walletId) {
					updateWalletKYBProgress(updatedData.walletId, updatedData);
				}
				setCurrentStep(step);
				setShowReview(false);
			}
		},
		[formData, calculateProgress, saveMutation, updateWalletKYBProgress],
	);

	const handleNext = async () => {
		if (showReview || !formData) return;

		const totalSteps = 6;

		if (currentStep < totalSteps) {
			goToStep(currentStep + 1);
		} else {
			// Save before Review so step 6 data is persisted (BFF submit validates stored doc)
			try {
				const updatedData = {
					...formData,
					currentStep: 6,
					percentComplete: calculateProgress({
						...formData,
						currentStep: 6,
					}),
				};
				await saveMutation.mutateAsync(updatedData);
				setFormData(updatedData);
				setShowReview(true);
			} catch (err) {
				notifications.error(
					err instanceof Error
						? err.message
						: "Failed to save. Please try again.",
				);
			}
		}
	};

	const handleBack = () => {
		if (showReview) {
			setValidationErrors(null);
			setShowReview(false);
			return;
		}

		if (!formData) return;

		if (currentStep > 1) {
			goToStep(currentStep - 1);
		}
	};

	const onInvalidSubmit = useCallback(() => {
		const errs = form.formState.errors as Record<string, unknown>;
		const fieldToStepMap = getFieldToStepMap({
			incorporationCountry: String(formData?.countryOfIncorporation ?? ""),
			entityType: String(formData?.entityType ?? ""),
			flowType: "KYB",
		});
		const flattened = flattenFormErrors(errs, fieldToStepMap);
		if (flattened.length === 0) {
			notifications.error("Please fix validation errors before submitting.");
			return;
		}
		setValidationErrors(flattened);
		const summary =
			flattened.length <= 3
				? flattened.map((e) => e.message).join(". ")
				: `${flattened
						.slice(0, 3)
						.map((e) => e.message)
						.join(". ")} ...and ${flattened.length - 3} more`;
		notifications.error(summary);
	}, [form, formData?.countryOfIncorporation, formData?.entityType]);

	const onValidSubmit = async (data: KYCFormData) => {
		if (!data) return;
		// Entry lock: ignore re-entrant submits (double-click, or a save path
		// firing mid-submit) before the mutation's isPending flag flips. Set
		// below before the first await; reset in the finally.
		if (submittingRef.current) return;

		// Config-driven validation (ownershipTiers, etc.) — schema handles format
		const configErrors = validateFormForSubmit(
			data as unknown as Record<string, unknown>,
		);
		if (configErrors.length > 0) {
			const flattened: FlattenedError[] = configErrors.map((msg) => {
				const match = msg.match(/\(Step (\d+)\)$/);
				const step = match ? Number(match[1]) : 1;
				return {
					path: "config",
					message: msg.replace(/\s*\(Step \d+\)$/, ""),
					step,
				};
			});
			setValidationErrors(flattened);
			notifications.error(
				`Please complete all required fields:\n\n${configErrors.slice(0, 10).join("\n")}${configErrors.length > 10 ? `\n\n...and ${configErrors.length - 10} more` : ""}`,
			);
			return;
		}

		// Save first — BFF submit validates the stored document; ensure all form data (including step 6) is persisted
		const formToSave = {
			...data,
			percentComplete: 100,
			status:
				formData?.status === "draft"
					? ("draft" as const)
					: ("in-progress" as const),
		};

		// Block debounced autosave for the duration of the submit so a trailing
		// save can't revert the submitted status (reset in finally below).
		submittingRef.current = true;
		// Completion contract: save_ok && submit_ok => form submitted; wallet_patch_ok tracked separately (non-blocking)
		try {
			try {
				await saveMutation.mutateAsync(formToSave);
			} catch (saveErr) {
				(saveErr as KybStagedError).stage = KYB_STAGES.SAVE;
				throw saveErr;
			}

			try {
				await submitMutation.mutateAsync(data.id);
			} catch (submitErr) {
				(submitErr as KybStagedError).stage = KYB_STAGES.SUBMIT;
				throw submitErr;
			}

			// Update wallet to mark KYB as completed (non-blocking: form is submitted even if this fails)
			let walletPatchOk = false;
			if (data.walletId) {
				const wallet = getWallet(data.walletId);
				if (wallet) {
					try {
						await updateWallet(data.walletId, {
							kyc_form_completed: {
								...wallet.kyc_form_completed,
								kyb_progress: 100,
								completed: true,
								completed_at: new Date().toISOString(),
								completed_by: user?.id,
							},
						});
						walletPatchOk = true;
					} catch (patchErr) {
						(patchErr as KybStagedError).stage = KYB_STAGES.WALLET_PATCH;
						// Form submitted successfully; wallet patch is non-blocking
						notifications.error(
							toUserMessage(patchErr, KYB_STAGES.WALLET_PATCH),
						);
						// Continue to success flow — form is submitted
					}
				}
			}

			setValidationErrors(null);
			setFormData({ ...formToSave, status: "submitted" as const });
			notifications.success(
				walletPatchOk
					? "Application submitted successfully!"
					: "Application submitted! (Wallet status update had an issue — it may already be correct.)",
			);

			// Redirect based on user role
			if (hasAdminAccess) {
				navigate("/kyc");
			} else {
				if (data.walletId) {
					navigate(`/wallet/${data.walletId}`);
				} else {
					navigate("/");
				}
			}
		} catch (err) {
			const stagedErr = err as KybStagedError;
			const stage = stagedErr.stage;
			const message = toUserMessage(err, stage);

			// Handle "already submitted" — refetch so UI shows submitted state, then redirect
			if (isAlreadySubmittedError(err)) {
				notifications.error(
					"This application was already submitted. Redirecting...",
				);
				const raw = await trpcUtils.kyb.getById.fetch({ id: data.id });
				const fresh = raw ? transformDBKYBToKYCFormData(raw) : null;
				if (fresh) setFormData(fresh);
				if (isAdmin) {
					navigate("/kyc");
				} else if (data.walletId) {
					navigate(`/wallet/${data.walletId}`);
				} else {
					navigate("/");
				}
				return;
			}

			// Handle 422 validation errors with fieldErrors from BFF (REST or tRPC)
			const errData =
				err instanceof ApiError ? err.data : (err as { data?: unknown }).data;
			if (errData && typeof errData === "object" && "fieldErrors" in errData) {
				const rawFieldErrors = (
					errData as { fieldErrors?: Record<string, string> }
				).fieldErrors;
				if (rawFieldErrors && typeof rawFieldErrors === "object") {
					const fieldToStepMap = getFieldToStepMap({
						incorporationCountry: String(data.countryOfIncorporation ?? ""),
						entityType: String(data.entityType ?? ""),
						flowType: "KYB",
					});
					const flattened: FlattenedError[] = Object.entries(
						rawFieldErrors,
					).map(([key, msg]) => {
						const uiKey = key.includes("_") ? dbKeyToUiKey(key) : key;
						return {
							path: uiKey,
							message: msg,
							step: fieldToStepMap[uiKey] ?? 1,
						};
					});
					if (flattened.length > 0) {
						setValidationErrors(flattened);
						const summary =
							flattened.length <= 3
								? flattened.map((e) => e.message).join(". ")
								: `${flattened
										.slice(0, 3)
										.map((e) => e.message)
										.join(". ")} ...and ${flattened.length - 3} more`;
						notifications.error(summary);
						return;
					}
				}
			}

			notifications.error(message);
		} finally {
			submittingRef.current = false;
		}
	};

	const handleApprove = async (approverName: string) => {
		if (!formData || !approverName.trim()) {
			notifications.error(
				"Please enter your name to approve this application.",
			);
			return;
		}

		// Resolve walletId: form may have wallet_id null (legacy); fallback to URL when opened from wallet
		const walletIdFromUrl = new URLSearchParams(location.search).get(
			"walletId",
		);
		const effectiveWalletId = formData.walletId ?? walletIdFromUrl ?? undefined;

		try {
			// Add approval comment
			await addCommentMutation.mutateAsync({
				formId: formData.id,
				author: approverName,
				text: `Application approved by ${approverName}`,
				isInternal: true,
			});

			// Update form status to approved with 100% progress (include walletId so BFF can sync wallet when form has wallet_id: null)
			const updatedForm = {
				...formData,
				walletId: effectiveWalletId ?? formData.walletId,
				percentComplete: 100,
				status: "approved" as const,
				approvedBy: approverName,
				approvedAt: new Date().toISOString(),
			};
			await saveMutation.mutateAsync(updatedForm);

			// Update wallet to mark KYB task as completed (BFF also does this; frontend ensures it when wallet is not in context)
			if (effectiveWalletId) {
				const wallet = getWallet(effectiveWalletId);
				if (wallet) {
					await updateWallet(effectiveWalletId, {
						kyc_form_completed: {
							...wallet.kyc_form_completed,
							kyb_progress: 100,
							completed: true,
							completed_at: updatedForm.approvedAt,
							completed_by: approverName,
						},
					});
				}
			}

			setFormData(updatedForm);
			setShowApprovalModal(false);
			notifications.success("Application approved successfully!");
		} catch (err) {
			const message =
				err instanceof ApiError
					? err.message
					: err instanceof Error
						? err.message
						: "Approval failed. Please try again.";
			notifications.error(message);
		}
	};

	const handleAddComment = async () => {
		if (formData && newComment.trim() && commentAuthor.trim()) {
			await addCommentMutation.mutateAsync({
				formId: formData.id,
				author: commentAuthor,
				text: newComment,
				isInternal: isInternalComment,
			});
			setNewComment("");
			formInitializedFromQueryRef.current = null;
			queryClient.invalidateQueries({ queryKey: ["kyb", formData.id] });
		}
	};

	if (!formData) {
		return (
			<div className="flex min-h-[400px] items-center justify-center">
				<LoadingSpinner size="md" message="Loading form..." />
			</div>
		);
	}

	// Calculate after formData check
	const totalSteps = 6;
	const _progress = showReview ? 100 : (currentStep / totalSteps) * 100;
	const _progressPercent = formData.percentComplete || 0;

	const renderStep = () => {
		// If form is submitted or approved, always show review (read-only)
		if (
			showReview ||
			formData.status === "submitted" ||
			formData.status === "approved"
		) {
			const stepsWithErrors = validationErrors
				? [...new Set(validationErrors.map((e) => e.step))]
				: undefined;
			return (
				<KYCReview
					formData={formData}
					stepsWithErrors={stepsWithErrors}
					onEdit={(configStep) => {
						// Only allow editing if not submitted/approved
						if (
							formData.status !== "submitted" &&
							formData.status !== "approved"
						) {
							setValidationErrors(null);
							const displayStep =
								CONFIG_TO_DISPLAY_STEP[configStep] ?? configStep;
							goToStep(displayStep);
						}
					}}
				/>
			);
		}

		switch (currentStep) {
			case 1:
				return (
					<Step1CompanyBasics
						formData={formData}
						updateFormData={updateFormData}
					/>
				);
			case 2:
				return (
					<Step3Ownership formData={formData} updateFormData={updateFormData} />
				);
			case 3:
				return (
					<Step4Compliance
						formData={formData}
						updateFormData={updateFormData}
					/>
				);
			case 4:
				return (
					<Step5Limits formData={formData} updateFormData={updateFormData} />
				);
			case 5:
				return (
					<Step2WalletTechnical
						formData={formData}
						updateFormData={updateFormData}
					/>
				);
			case 6:
				return (
					<Step6Contacts formData={formData} updateFormData={updateFormData} />
				);
			default:
				return null;
		}
	};

	const _getStepTitle = () => {
		if (showReview) return "Review & Submit";

		const titles = [
			"Company Basics",
			"Ownership & UBO",
			"Compliance & Risk",
			"Limits & Restrictions",
			"Wallet & Technical Details",
			"Contacts & Final Uploads",
		];

		return titles[currentStep - 1] || "";
	};

	// Check if a specific step is complete based on required fields
	const isStepComplete = (stepNumber: number): boolean => {
		if (!formData) return false;

		const isFilled = (value: unknown): boolean => {
			if (value === undefined || value === null) return false;
			if (typeof value === "string") return value.trim() !== "";
			if (typeof value === "boolean") return true;
			if (typeof value === "number") return true;
			if (Array.isArray(value)) return value.length > 0;
			if (typeof value === "object") {
				const obj = value as Record<string, unknown>;
				if (obj.name !== undefined) {
					return Boolean(obj.name && String(obj.name).trim() !== "");
				}
				return Object.values(obj).some((v) =>
					Boolean(v && String(v).trim() !== ""),
				);
			}
			return false;
		};

		switch (stepNumber) {
			case 1: {
				const step1Required = [
					"businessLegalName",
					"entityType",
					"countryOfIncorporation",
					"dateOfIncorporation",
					"website",
					"businessDescription",
					"expectedMonthlyVolume",
					"registeredAddress",
					"proofOfBusinessUrl",
				];
				return step1Required.every((field) =>
					isFilled((formData as unknown as Record<string, unknown>)[field]),
				);
			}
			case 2: {
				const step2Required = [
					"numberOfOwnershipTiers",
					"consentForBackgroundChecks",
					"anyOwnerConvicted",
				];
				const basicFieldsComplete = step2Required.every((field) =>
					isFilled((formData as unknown as Record<string, unknown>)[field]),
				);

				// Check if individual owners in Georgia, Nevada, or Canada have uploaded consent forms
				const allOwnersHaveRequiredConsent = formData.ownershipTiers.every(
					(tier) => {
						if (tier.ownerType !== "Individual") return true; // Skip corporate owners
						if ((tier.ownershipPercentage ?? 0) < 10) return true; // Skip if ownership < 10%

						// Georgia or Nevada: requires 1 consent form
						if (
							tier.countryOfResidence === "US" &&
							(tier.stateOrProvince === "Georgia" ||
								tier.stateOrProvince === "Nevada")
						) {
							return !!tier.consentFormUrl;
						}

						// Canada: requires 2 consent forms
						if (tier.countryOfResidence === "CA") {
							return !!tier.consentFormUrl && !!tier.secondConsentFormUrl;
						}

						return true; // No consent required for other locations
					},
				);

				return basicFieldsComplete && allOwnersHaveRequiredConsent;
			}
			case 3: {
				const step3Required = [
					"isRegulatedEntity",
					"complianceOfficer",
					"sanctionsScreening",
					"blockchainMonitoring",
					"regulatoryEnforcement",
				];
				return step3Required.every((field) =>
					isFilled((formData as unknown as Record<string, unknown>)[field]),
				);
			}
			case 4: {
				const step4Required = [
					"velocityLimits",
					"transactionLimits",
					"blockWalletHolders",
				];
				return step4Required.every((field) =>
					isFilled((formData as unknown as Record<string, unknown>)[field]),
				);
			}
			case 5: {
				const step5Required = [
					"walletType",
					"currentlyIntegratedWithStellar",
					"allowP2PInternal",
					"allowP2PExternal",
					"virtualAssetsDetails",
					"publicKeySharing",
					"isWalletOwnerKYCRequired",
				];
				return step5Required.every((field) =>
					isFilled((formData as unknown as Record<string, unknown>)[field]),
				);
			}
			case 6: {
				const step6Required = [
					"primaryBusinessContact",
					"primaryProductContact",
					"primaryLegalContact",
					"primarySigner",
				];
				return step6Required.every((field) =>
					isFilled((formData as unknown as Record<string, unknown>)[field]),
				);
			}
			default:
				return false;
		}
	};

	return (
		<FormProvider {...form}>
			<div
				className={cn(
					"mx-auto flex min-h-screen w-full max-w-[900px] flex-col overflow-x-hidden px-4 pt-24 pb-6",
					(formData.status === "submitted" || formData.status === "approved") &&
						"pb-24",
				)}
			>
				{/* Header with Progress */}
				<div className="mb-4">
					<div className="mb-3 flex items-center justify-between">
						<h1 className="text-2xl font-bold text-(--color-foreground) normal-case">
							KYB Application
						</h1>
						<div className="flex items-center gap-3">
							{/* Auto-save indicator */}
							{saveMutation.isPending && (
								<span className="inline-flex items-center gap-1.5 font-medium text-(--color-muted-foreground) text-[13px]">
									<div className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-(--color-primary-200) border-2 border-t-(--color-primary-500)" />
									Saving...
								</span>
							)}

							{/* Close button (subtle) - Only show if not submitted or approved */}
							{formData.status !== "submitted" &&
								formData.status !== "approved" && (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={async () => {
											if (!formData || submittingRef.current) return;
											try {
												// Save before closing
												const progress = calculateProgress(formData);
												const updatedForm = {
													...formData,
													currentStep: currentStep,
													percentComplete: progress,
													lastSavedAt: new Date().toISOString(),
												};
												await saveMutation.mutateAsync(updatedForm);
												if (updatedForm.walletId) {
													try {
														await updateWalletKYBProgress(
															updatedForm.walletId,
															updatedForm,
														);
													} catch {
														// Don't block navigation if wallet update fails
													}
												}
												// Redirect based on role
												if (hasAdminAccess) {
													if (updatedForm.walletId) {
														navigate(`/wallet/${updatedForm.walletId}`);
													} else {
														navigate("/kyc");
													}
												} else {
													navigate("/");
												}
											} catch (err) {
												const message =
													err instanceof ApiError
														? err.message
														: err instanceof Error
															? err.message
															: "Failed to save. Please try again.";
												notifications.error(message);
											}
										}}
									>
										Close
									</Button>
								)}

							{/* Close button for submitted/approved */}
							{(formData.status === "submitted" ||
								formData.status === "approved") && (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => {
										if (hasAdminAccess) {
											if (formData.walletId) {
												navigate(`/wallet/${formData.walletId}`);
											} else {
												navigate("/kyc");
											}
										} else {
											navigate("/");
										}
									}}
								>
									Close
								</Button>
							)}

							{/* Admin can delete draft forms - Icon only */}
							{isAdmin &&
								(formData.status === "draft" ||
									formData.status === "in-progress") && (
									<Button
										type="button"
										variant="outline"
										size="icon"
										className="border-(--color-error-200) text-(--color-error-500) hover:border-(--color-error-500) hover:bg-(--color-error-500) hover:text-white"
										onClick={async () => {
											const ok = await confirm({
												title: "Delete Draft",
												message:
													"Delete this draft KYB application? This cannot be undone.",
												confirmLabel: "Delete",
												variant: "danger",
											});
											if (!ok) return;
											try {
												await deleteMutation.mutateAsync(formData.id);
												// Clear wallet link so "Start" shows again
												const walletId =
													formData.walletId ??
													wallets.find(
														(w) =>
															w.kyc_form_completed?.kyb_form_id === formData.id,
													)?.id;
												if (walletId) {
													const wallet = getWallet(walletId);
													if (
														wallet?.kyc_form_completed?.kyb_form_id ===
														formData.id
													) {
														await updateWallet(walletId, {
															kyc_form_completed: {
																...wallet.kyc_form_completed,
																kyb_form_id: undefined,
																kyb_progress: undefined,
															},
														});
													}
												}
												navigate("/kyc");
											} catch (err) {
												notifications.error(
													err instanceof Error
														? err.message
														: "Failed to delete",
												);
											}
										}}
										title="Delete draft"
										aria-label="Delete draft"
									>
										<Trash2 size={18} />
									</Button>
								)}
						</div>
					</div>

					{/* Step Navigation */}
					{formData.status !== "submitted" &&
						formData.status !== "approved" &&
						!showReview && (
							<div className="mt-2 mb-4 flex items-center justify-center gap-3 overflow-x-auto px-2 py-3">
								{[1, 2, 3, 4, 5, 6].map((step, index) => {
									const hasVisited = step < currentStep;
									const isActive = step === currentStep;
									const isCompleted = hasVisited && isStepComplete(step);
									const isIncomplete = hasVisited && !isStepComplete(step);
									const stepLabels = [
										"Company",
										"Ownership",
										"Compliance",
										"Limits",
										"Wallet",
										"Contacts",
									];

									return (
										<React.Fragment key={step}>
											{index > 0 && (
												<div
													className={cn(
														"mb-7 h-0.5 w-10 min-w-[30px] flex-shrink-0",
														isCompleted
															? "bg-(--color-success-500)"
															: "bg-(--color-border)",
													)}
												/>
											)}
											{/* biome-ignore lint/a11y/useSemanticElements: Step nav item needs to be a div for styling */}
											<div
												className={cn(
													"flex flex-shrink-0 cursor-pointer flex-col items-center gap-2 transition-all",
												)}
												role="button"
												tabIndex={0}
												onClick={() => goToStep(step)}
												onKeyDown={(e) => {
													if (e.key === "Enter" || e.key === " ") {
														e.preventDefault();
														goToStep(step);
													}
												}}
											>
												<div
													className={cn(
														"flex h-10 w-10 items-center justify-center rounded-full border-2 border-(--color-border) bg-(--color-card) font-semibold text-[15px] text-(--color-muted-foreground) shadow-sm transition-all",
														isActive &&
															"scale-105 border-(--color-cta-primary) bg-(--color-cta-primary) text-(--color-cta-primary-foreground) shadow-md",
														isCompleted &&
															!isActive &&
															"border-(--color-success-500) bg-(--color-success-500) text-white",
														isIncomplete &&
															!isActive &&
															"border-(--color-warning-500) bg-(--color-warning-500)/10 text-(--color-warning-500)",
													)}
												>
													{isCompleted ? (
														<Check size={18} />
													) : isIncomplete ? (
														<AlertCircle size={18} />
													) : (
														step
													)}
												</div>
												<span
													className={cn(
														"max-w-[80px] truncate text-center text-(--color-muted-foreground) text-xs leading-tight",
														isActive &&
															"font-semibold text-(--color-cta-primary)",
													)}
												>
													{stepLabels[step - 1]}
												</span>
											</div>
										</React.Fragment>
									);
								})}
							</div>
						)}
				</div>

				{/* Comments Sidebar */}
				{showComments && (
					<div className="fixed top-0 right-0 z-[1000] flex h-screen w-[400px] max-w-full flex-col border-(--color-border) border-l-2 bg-white shadow-xl">
						<div className="flex items-center justify-between border-(--color-border) border-b-2 px-5 py-5">
							<h3>Comments & Notes</h3>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={() => setShowComments(false)}
							>
								<X size={16} />
							</Button>
						</div>

						<div className="flex-1 overflow-y-auto p-5">
							{formData?.reviewerComments &&
							formData.reviewerComments.length > 0 ? (
								formData.reviewerComments.map((comment) => (
									<div
										key={comment.id}
										className={cn(
											"mb-3 rounded-(--radius) border border-(--color-border) bg-(--color-muted) p-4",
											comment.isInternal &&
												"border-(--color-warning-200) bg-(--color-warning-50)",
										)}
									>
										<div className="mb-2 flex items-center justify-between">
											<strong>{comment.author}</strong>
											{comment.isInternal && (
												<span className="rounded-full bg-(--color-warning-500) px-2 py-0.5 font-semibold text-white text-xs">
													Internal
												</span>
											)}
										</div>
										<p className="mb-2 whitespace-pre-wrap text-(--color-foreground) text-sm">
											{comment.text}
										</p>
										<span className="text-(--color-muted-foreground) text-xs">
											{new Date(comment.timestamp).toLocaleString()}
										</span>
									</div>
								))
							) : (
								<p className="py-10 text-center text-(--color-muted-foreground)">
									No comments yet
								</p>
							)}
						</div>

						<div className="flex flex-col gap-2 border-(--color-border) border-t-2 bg-white p-5">
							<Input
								type="text"
								aria-label="Your name"
								placeholder="Your name"
								value={commentAuthor}
								onChange={(e) => setCommentAuthor(e.target.value)}
								className="mb-2"
							/>
							<Textarea
								placeholder="Add a comment or note..."
								value={newComment}
								onChange={(e) => setNewComment(e.target.value)}
								rows={3}
							/>
							<div className="mt-2 flex items-center gap-3">
								<label className="flex items-center gap-1.5 text-sm">
									<input
										type="checkbox"
										checked={isInternalComment}
										onChange={(e) => setIsInternalComment(e.target.checked)}
									/>
									Internal comment (not visible to applicant)
								</label>
							</div>
							<Button
								type="button"
								size="sm"
								onClick={handleAddComment}
								disabled={!newComment.trim() || !commentAuthor.trim()}
								className="mt-3 w-full"
							>
								<Send size={16} />
								Add Comment
							</Button>
						</div>
					</div>
				)}

				{/* Field-change disclosure banner */}
				{showContextChangeBanner && !showReview && (
					<div className="mb-4 flex items-center gap-2.5 rounded-(--radius) border border-(--color-info-300) bg-(--color-info-50) px-4 py-2.5 text-(--color-info-700) text-sm">
						<Info size={16} className="shrink-0" />
						<span className="flex-1">
							Some fields have been updated based on your country and business
							type selection.
						</span>
						<button
							type="button"
							onClick={() => setShowContextChangeBanner(false)}
							className="flex items-center"
							aria-label="Dismiss"
						>
							<X size={14} />
						</button>
					</div>
				)}

				{/* Form Content */}
				<div className="mb-5 w-full rounded-(--radius-lg) border border-(--color-border) bg-white p-6 shadow-sm">
					{renderStep()}
				</div>

				{/* Accessibility: announce validation errors to screen readers */}
				{form.formState.errors &&
					Object.keys(form.formState.errors).length > 0 &&
					formData && (
						<div
							role="alert"
							aria-live="polite"
							className="sr-only"
							aria-atomic="true"
						>
							{flattenFormErrors(
								form.formState.errors as Record<string, unknown>,
								getFieldToStepMap({
									incorporationCountry: String(
										formData.countryOfIncorporation ?? "",
									),
									entityType: String(formData.entityType ?? ""),
									flowType: "KYB",
								}),
							)
								.slice(0, 3)
								.map((e) => e.message)
								.join(". ")}
						</div>
					)}

				{/* Navigation Footer - sticky when submitted/approved so Approve button is always visible */}
				<div
					className={cn(
						"flex items-center justify-between border-(--color-border) border-t-2 py-6",
						(formData.status === "submitted" ||
							formData.status === "approved") &&
							"fixed right-0 bottom-0 left-0 z-[100] border-(--color-border) border-t-2 bg-white px-6 py-5 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]",
					)}
				>
					{/* Only show Back button if not submitted/approved or if admin viewing */}
					{formData.status !== "submitted" &&
						formData.status !== "approved" && (
							<Button
								type="button"
								variant="outline"
								onClick={handleBack}
								disabled={currentStep === 1 && !showReview}
							>
								<ChevronLeft size={18} />
								Back
							</Button>
						)}

					<div className="text-(--color-muted-foreground) text-sm">
						{formData.status === "submitted" && (
							<span className="font-semibold text-(--color-warning-500)">
								Status: Submitted - Awaiting Approval
							</span>
						)}
						{formData.status === "approved" && (
							<span className="font-semibold text-(--color-success-500)">
								Status: Approved
							</span>
						)}
						{formData.status !== "submitted" &&
							formData.status !== "approved" && (
								<span>
									Last saved:{" "}
									{new Date(formData.lastSavedAt).toLocaleTimeString()}
								</span>
							)}
					</div>

					{showReview ||
					formData.status === "submitted" ||
					formData.status === "approved" ? (
						<>
							{/* Show Submit button only for partners with draft/in-progress status */}
							{!isAdmin &&
								formData.status !== "submitted" &&
								formData.status !== "approved" && (
									<Button
										type="button"
										onClick={() => onValidSubmit(formData)}
										disabled={submitMutation.isPending}
									>
										{submitMutation.isPending
											? "Submitting..."
											: "Submit Application"}
									</Button>
								)}

							{/* Admin can submit on behalf of partner when form is still draft/in-progress.
							   Hits the same submissions endpoint; backend detects admin actor,
							   tags the form with submitted_on_behalf=true, and adds an internal
							   audit comment. Subject to the same required-field validation. */}
							{isAdmin &&
								formData.status !== "submitted" &&
								formData.status !== "approved" && (
									<Button
										type="button"
										variant="outline"
										onClick={async () => {
											const ok = await confirm({
												title: "Submit on Behalf of Partner",
												message:
													"This will submit the application as if the partner clicked Submit. The form must have all required fields filled. Continue?",
												confirmLabel: "Submit on Behalf",
											});
											if (ok) onValidSubmit(formData);
										}}
										disabled={submitMutation.isPending}
									>
										{submitMutation.isPending
											? "Submitting..."
											: "Submit on Behalf"}
									</Button>
								)}

							{/* Show Approve button for admins viewing submitted applications */}
							{isAdmin && formData.status === "submitted" && (
								<Button
									type="button"
									onClick={() => setShowApprovalModal(true)}
								>
									Approve Application
								</Button>
							)}

							{/* Export buttons for completed forms (submitted or approved) - admin can download before approving */}
							{isAdmin &&
								(formData.status === "submitted" ||
									formData.status === "approved") && (
									<>
										<Button
											type="button"
											variant="outline"
											size="sm"
											disabled={
												pdfJob.job.status === "pending" ||
												pdfJob.job.status === "processing"
											}
											title="Download PDF summary"
											onClick={() => {
												pdfJob.start(
													`/api/kyb-forms/${formData.id}/export/pdf`,
												);
											}}
										>
											{pdfJob.job.status === "pending" ||
											pdfJob.job.status === "processing" ? (
												<LoadingSpinner size="sm" />
											) : (
												<Download size={16} />
											)}
											Download PDF
										</Button>
										<Button
											type="button"
											variant="outline"
											size="sm"
											disabled={
												bundleJob.job.status === "pending" ||
												bundleJob.job.status === "processing"
											}
											title="Download attachments ZIP"
											onClick={() => {
												bundleJob.start(
													`/api/kyb-forms/${formData.id}/export/attachments`,
												);
											}}
										>
											{bundleJob.job.status === "pending" ||
											bundleJob.job.status === "processing" ? (
												<LoadingSpinner size="sm" />
											) : (
												<Archive size={16} />
											)}
											Download Attachments
										</Button>
									</>
								)}
						</>
					) : (
						<Button type="button" onClick={handleNext}>
							{currentStep === totalSteps ? "Review" : "Next"}
							<ChevronRight size={18} />
						</Button>
					)}
				</div>

				{/* Admin Approval Modal */}
				<ApproveApplicationModal
					isOpen={showApprovalModal}
					onClose={() => setShowApprovalModal(false)}
					onConfirm={handleApprove}
					isPending={addCommentMutation.isPending || saveMutation.isPending}
				/>
			</div>
		</FormProvider>
	);
};

export default KYCForm;
